import { Suspense, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconButton } from '@cbhq/cds-web/buttons';
import { LogoMark } from '@cbhq/cds-web/icons/LogoMark';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Sidebar } from '@cbhq/cds-web/navigation/Sidebar';
import { SidebarItem } from '@cbhq/cds-web/navigation/SidebarItem';
import { TextHeadline } from '@cbhq/cds-web/typography/TextHeadline';
import { ProgressCircle } from '@cbhq/cds-web/visualizations/ProgressCircle';
import { NavigationBar } from '@cbhq/identity-navigation';
import { useSpectrum } from '@cbhq/identity-shared';

import { projectId, supportUrl, targetEnvironment } from '../configs/environments';
import { useOrganization } from '../contexts/OrganizationContext';
import { OrganizationProvider } from '../contexts/OrganizationProvider';
import { ROUTE_APPROVALS } from '../pages/Approvals/utils/constants';
import { ROUTE_SECURITY } from '../pages/Security/utils/constants';
import { useRumViewTracking } from '../utils/analytics/useRumViewTracking';
import { AppExperiments, useIsTreatment } from '../utils/experiments';

import { BasicErrorFallback } from './ErrorBoundary/BasicErrorFallback';
import { NoOrganizationsFallback } from './ErrorBoundary/NoOrganizationsFallback';
import { WorkspaceOrganizationPicker } from './WorkspaceOrganizationPicker';

const messages = defineMessages({
  help: {
    id: 'Navigation.help',
    defaultMessage: 'Help',
    description: 'Accessibility label for the help button in the navigation bar',
  },
  lightTheme: {
    id: 'Navigation.lightTheme',
    defaultMessage: 'Light',
    description: 'Label for the active light theme in the navigation profile menu',
  },
  darkTheme: {
    id: 'Navigation.darkTheme',
    defaultMessage: 'Dark',
    description: 'Label for the active dark theme in the navigation profile menu',
  },
  contentLoadingAccessibilityLabel: {
    id: 'Navigation.contentLoadingAccessibilityLabel',
    defaultMessage: 'Loading',
    description: 'Accessibility label while main navigation content is loading',
  },
});

type Props = {
  children: ReactNode;
};

export function NavigationContentSpinner() {
  const { formatMessage } = useIntl();

  return (
    <VStack flexGrow={1} width="100%" minHeight="100%" alignItems="center" justifyContent="center">
      <ProgressCircle
        size={4}
        indeterminate
        color="fgPrimary"
        accessibilityLabel={formatMessage(messages.contentLoadingAccessibilityLabel)}
      />
    </VStack>
  );
}

function NavigationLayout({ children }: Props) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  // Centralized, automatic RUM view tracking for all workspace routes.
  useRumViewTracking();
  const { spectrum, setSpectrumPreference } = useSpectrum();
  const { organizations, selectedOrganization, selectOrganization, error, retry } =
    useOrganization();
  const isWorkspaceAuditLogTreatment = useIsTreatment(AppExperiments.t0018_aac156e2);
  // The consensus experiment gates the Approvals sidebar item, and — together
  // with the domain-claims experiment (either one is enough) — the Security
  // sidebar item too, since Claim a domain lives inside Security.
  const isWorkspaceConsensusTreatment = useIsTreatment(AppExperiments.t0018_7b870e9f);
  const isClaimADomainTreatment = useIsTreatment(AppExperiments.t0018_1a1ab525);
  const isSecuritySidebarItemTreatment = isWorkspaceConsensusTreatment || isClaimADomainTreatment;
  const isWorkspaceSettingsTreatment = useIsTreatment(AppExperiments.t0018_3ab23203);
  const navigationTarget = useMemo(() => {
    const isLocalHost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    return isLocalHost ? 'local' : targetEnvironment;
  }, []);

  const isTeamActive = useMemo(
    () => location.pathname === '/team' || location.pathname.startsWith('/team/'),
    [location.pathname],
  );

  const isActivityActive = useMemo(
    () => location.pathname === '/activity' || location.pathname.startsWith('/activity/'),
    [location.pathname],
  );

  const isSecurityActive = useMemo(
    () =>
      location.pathname === ROUTE_SECURITY || location.pathname.startsWith(`${ROUTE_SECURITY}/`),
    [location.pathname],
  );

  const isApprovalsActive = useMemo(
    () =>
      location.pathname === ROUTE_APPROVALS || location.pathname.startsWith(`${ROUTE_APPROVALS}/`),
    [location.pathname],
  );

  const isSettingsActive = useMemo(
    () => location.pathname === '/settings' || location.pathname.startsWith('/settings/'),
    [location.pathname],
  );

  const handleTeamPress = useCallback(() => {
    navigate('/team');
  }, [navigate]);

  const handleActivityPress = useCallback(() => {
    navigate('/activity');
  }, [navigate]);

  const handleSecurityPress = useCallback(() => {
    navigate(ROUTE_SECURITY);
  }, [navigate]);

  const handleApprovalsPress = useCallback(() => {
    navigate(ROUTE_APPROVALS);
  }, [navigate]);

  const handleSettingsPress = useCallback(() => {
    navigate('/settings');
  }, [navigate]);

  const handleThemeToggle = useCallback(() => {
    setSpectrumPreference(spectrum === 'light' ? 'dark' : 'light');
  }, [setSpectrumPreference, spectrum]);

  const themeToggleProps = useMemo(() => {
    return {
      onThemeToggle: handleThemeToggle,
      activeThemeLabel:
        spectrum === 'light'
          ? formatMessage(messages.lightTheme)
          : formatMessage(messages.darkTheme),
      themeToggleValue: spectrum,
    };
  }, [formatMessage, handleThemeToggle, spectrum]);

  const sidebarLogo = useMemo(
    () => (
      <HStack alignItems="center" gap={1}>
        <LogoMark size={32} />
        <TextHeadline as="span" font="title4" textTransform="uppercase">
          Workspace
        </TextHeadline>
      </HStack>
    ),
    [],
  );

  const hasNoOrganizations = organizations?.length === 0;

  let content: ReactNode;
  if (error) {
    content = <BasicErrorFallback retry={retry} error={error} />;
  } else if (organizations === undefined) {
    content = <NavigationContentSpinner />;
  } else if (hasNoOrganizations) {
    content = <NoOrganizationsFallback />;
  } else {
    content = <Suspense fallback={<NavigationContentSpinner />}>{children}</Suspense>;
  }

  if (hasNoOrganizations) {
    return (
      <VStack height="100vh">
        <NavigationBar
          {...themeToggleProps}
          projectId={projectId}
          action="web-Workspace-LoggedInHeader"
          target={navigationTarget}
          isUserSwitcher
          end={
            <IconButton
              name="helpCenterQuestionMark"
              accessibilityLabel={formatMessage(messages.help)}
              href={supportUrl}
              target="_blank"
              as="a"
            />
          }
        >
          {sidebarLogo}
        </NavigationBar>
        <Box flexGrow={1} overflow="auto" padding={4}>
          {content}
        </Box>
      </VStack>
    );
  }

  return (
    <HStack alignItems="stretch" overflow="hidden" height="100vh">
      <Sidebar logo={sidebarLogo}>
        {[
          <SidebarItem
            key="team"
            title="Team"
            icon="concierge"
            active={isTeamActive}
            onClick={handleTeamPress}
          />,
          ...(isWorkspaceAuditLogTreatment
            ? [
                <SidebarItem
                  key="activity"
                  title="Activity"
                  icon="pulse"
                  active={isActivityActive}
                  onClick={handleActivityPress}
                />,
              ]
            : []),
          ...(isSecuritySidebarItemTreatment
            ? [
                <SidebarItem
                  key="security"
                  title="Security"
                  icon="shieldOutline"
                  active={isSecurityActive}
                  onClick={handleSecurityPress}
                />,
              ]
            : []),
          ...(isWorkspaceConsensusTreatment
            ? [
                <SidebarItem
                  key="approvals"
                  title="Approvals"
                  icon="checkboxChecked"
                  active={isApprovalsActive}
                  onClick={handleApprovalsPress}
                />,
              ]
            : []),
          ...(isWorkspaceSettingsTreatment
            ? [
                <SidebarItem
                  key="settings"
                  title="Settings"
                  icon="gear"
                  active={isSettingsActive}
                  onClick={handleSettingsPress}
                />,
              ]
            : []),
        ]}
      </Sidebar>

      <VStack flexGrow={1} height="100%">
        <NavigationBar
          {...themeToggleProps}
          projectId={projectId}
          action="web-Workspace-LoggedInHeader"
          target={navigationTarget}
          isUserSwitcher
          end={
            <HStack gap={1}>
              <IconButton
                name="helpCenterQuestionMark"
                accessibilityLabel={formatMessage(messages.help)}
                href={supportUrl}
                target="_blank"
                as="a"
              />
              <WorkspaceOrganizationPicker
                organizations={organizations}
                selectedOrganization={selectedOrganization}
                selectOrganization={selectOrganization}
              />
            </HStack>
          }
        >
          {null}
        </NavigationBar>
        <Box flexGrow={1} overflow="auto" padding={4}>
          {content}
        </Box>
      </VStack>
    </HStack>
  );
}

export function Navigation({ children }: Props) {
  const location = useLocation();

  if (location.pathname.startsWith('/local-dev/')) {
    // eslint-disable-next-line react/jsx-no-useless-fragment -- Fragment is required to satisfy JSX element return type since ReactNode includes undefined
    return <>{children}</>;
  }

  return (
    <OrganizationProvider>
      <NavigationLayout>{children}</NavigationLayout>
    </OrganizationProvider>
  );
}
